import { lazy, Suspense } from "react"
import { Navigate, Route, Routes } from "react-router-dom"
import { Loader2 } from "lucide-react"

import AppShell from "@/layouts/AppShell"
import Dashboard from "@/pages/Dashboard"
import Login from "@/pages/Login"
import PlaceholderPage from "@/pages/PlaceholderPage"
import ProtectedRoute from "@/components/ProtectedRoute"
import Users from "@/pages/Users"
import Videos from "@/pages/Videos"
import NewVideo from "@/pages/NewVideo"
import EditVideo from "@/pages/EditVideo"
import Albums from "@/pages/Albums"
import AlbumForm from "@/pages/AlbumForm"
import Chapters from "@/pages/Chapters"
import ChapterForm from "@/pages/ChapterForm"
import Categories from "@/pages/Categories"
import CategoryForm from "@/pages/CategoryForm"
import Coaches from "@/pages/Coaches"
import CoachForm from "@/pages/CoachForm"
import CreatorApplications from "@/pages/CreatorApplications"
import Messages from "@/pages/Messages"
import MessageCompose from "@/pages/MessageCompose"
import Conversations from "@/pages/Conversations"
import Purposes from "@/pages/Purposes"
import PurposeForm from "@/pages/PurposeForm"
import Subscriptions from "@/pages/Subscriptions"
import Reports from "@/pages/Reports"
import ContactInbox from "@/pages/ContactInbox"

// Lazy-loaded — pulls in TipTap (~110KB gzipped) only when needed.
const Legal = lazy(() => import("@/pages/Legal"))

function PageFallback() {
  return (
    <div className="grid h-64 place-items-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  )
}

/**
 * Top-level routes for admin-v2.
 *
 * Path conventions (M7-redesign):
 *   - Plural collection routes: /videos, /albums, /chapters, /users, ...
 *     (was /user_video, /album, /upload_video, etc. in the old admin —
 *     inconsistent and reflected the backend route names rather than the
 *     user's mental model.)
 *   - Edit pages nested under their collection: /videos/:id, /albums/:id.
 *     (Old admin used /update_video/:id, /updateAlbum/:id, etc. — verb-first
 *     URLs that don't compose.)
 *
 * The OLD admin (apps/admin) keeps its existing routes; cutover routing is
 * decided at deployment time (nginx). For now this is a parallel app that
 * we run on a different port locally.
 *
 * Auth wiring (Login + ProtectedRoute) lands in Batch B.
 * Each page below currently renders a Placeholder so the shell is testable
 * end to end.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Public */}
      <Route path="/login" element={<Login />} />

      {/* Authenticated shell — every nested route requires a valid token. */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Content */}
        <Route path="/videos" element={<Videos />} />
        <Route path="/videos/new" element={<NewVideo />} />
        <Route path="/videos/:id" element={<EditVideo />} />
        <Route path="/albums" element={<Albums />} />
        <Route path="/albums/new" element={<AlbumForm />} />
        <Route path="/albums/:id" element={<AlbumForm />} />
        <Route path="/chapters" element={<Chapters />} />
        <Route path="/chapters/new" element={<ChapterForm />} />
        <Route path="/chapters/:id" element={<ChapterForm />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/categories/new" element={<CategoryForm />} />
        <Route path="/categories/:id" element={<CategoryForm />} />
        <Route path="/coaches" element={<Coaches />} />
        <Route path="/coaches/new" element={<CoachForm />} />
        <Route path="/coaches/:id" element={<CoachForm />} />
        <Route
          path="/creator-applications"
          element={<CreatorApplications />}
        />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/new" element={<MessageCompose />} />
        <Route path="/questions" element={<Conversations />} />
        <Route path="/purposes" element={<Purposes />} />
        <Route path="/purposes/new" element={<PurposeForm />} />
        <Route path="/purposes/:id" element={<PurposeForm />} />

        {/* People */}
        <Route path="/users" element={<Users />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/inbox" element={<ContactInbox />} />

        {/* System */}
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route
          path="/legal"
          element={
            <Suspense fallback={<PageFallback />}>
              <Legal />
            </Suspense>
          }
        />
        <Route path="/security" element={<PlaceholderPage name="Security" />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
